import { db } from '../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Save user notification preferences
 * @param {string} userId - The user's UID
 * @param {Object} notifications - The notification preferences object
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserNotificationPreferences = async (userId, notifications) => {
  try {
    const userDocRef = doc(db, 'internal_users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      await updateDoc(userDocRef, {
        notifications,
        lastUpdated: new Date().toISOString()
      });
    } else {
      // Create user document if it doesn't exist
      await setDoc(userDocRef, {
        uid: userId,
        notifications,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
    }
    
    return {
      success: true,
      message: 'Notification preferences saved successfully'
    };
  } catch (error) {
    console.error('Error saving notification preferences:', error);
    throw error;
  }
};

/**
 * Get user notification preferences
 * @param {string} userId - The user's UID
 * @param {string} collectionName - The collection name ('internal_users' or 'users')
 * @returns {Promise<Object>} User notification preferences
 */
export const getUserNotificationPreferences = async (userId, collectionName = 'internal_users') => {
  try {
    const userDocRef = doc(db, collectionName, userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.notifications || {};
    }
    
    return {};
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    throw error;
  }
};

/**
 * Save user notification preferences for portal users
 * @param {string} userId - The user's UID
 * @param {Object} notifications - The notification preferences object
 * @param {string} collectionName - The collection name ('internal_users' or 'users')
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveUserNotificationPreferencesForCollection = async (userId, notifications, collectionName = 'internal_users') => {
  try {
    const payloadNotifications =
      collectionName === 'users' ? omitInternalOnlyNotificationKeys(notifications) : notifications;
    const userDocRef = doc(db, collectionName, userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      await updateDoc(userDocRef, {
        notifications: payloadNotifications,
        lastUpdated: new Date().toISOString()
      });
    } else {
      // Create user document if it doesn't exist
      await setDoc(userDocRef, {
        uid: userId,
        notifications: payloadNotifications,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
    }
    
    return {
      success: true,
      message: 'Notification preferences saved successfully'
    };
  } catch (error) {
    console.error('Error saving notification preferences:', error);
    throw error;
  }
};

/** CRM / internal alert types must only email internal_users, never portal `users`. */
const INTERNAL_ONLY_NOTIFICATION_TYPES = new Set([
  'technologyFulfillment',
  'hrFulfillment',
  'marketingFulfillment',
  'generalFulfillment',
  'clientCheckInComplete',
]);

function omitInternalOnlyNotificationKeys(notifications) {
  if (!notifications || typeof notifications !== 'object') return notifications;
  const next = { ...notifications };
  INTERNAL_ONLY_NOTIFICATION_TYPES.forEach((key) => {
    delete next[key];
  });
  return next;
}

const sendNotificationEmailFn = httpsCallable(functions, 'sendNotificationEmail');

/**
 * Ask the server to email an audience. Clients can only read their own company's
 * profiles (firestore.rules), so the server reads recipients' preferences and addresses
 * (functions/notificationAudience.js).
 * @param {Object} audience - { kind: 'uids', uids } | { kind: 'names', names } | { kind: 'optedIn' }
 */
const notifyAudience = async (notificationType, data, audience) => {
  const result = await sendNotificationEmailFn({ notificationType, data, audience });
  return result.data;
};

/**
 * Send notification email to users
 * @param {string} notificationType - The type of notification
 * @param {Object} data - The data for the notification
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendNotificationEmail = async (notificationType, data) => {
  try {
    // Question / fire / message: only targeted recipients (never org-wide opt-in blast)
    if (notificationType === 'newQuestion') {
      return await sendQuestionNotification(data);
    }

    if (notificationType === 'newFire') {
      return await sendFireNotification(data);
    }

    if (notificationType === 'newMessage') {
      return await sendMessageNotification(data);
    }

    // Special handling for task notifications - only notify assignees
    if (notificationType === 'newTask' && data.assignees) {
      return await sendTaskNotification(data);
    }

    // Everyone who has this notification enabled
    return await notifyAudience(notificationType, data, { kind: 'optedIn' });
  } catch (error) {
    console.error('Error sending notification email:', error);
    throw error;
  }
};

/**
 * Send question notification to the assigned user only (if they have new question emails enabled).
 * @param {Object} questionData - The question data
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendQuestionNotification = async (questionData) => {
  try {
    if (!questionData.assigneeId) {
      return { success: true, message: 'No assignee to notify', sentCount: 0 };
    }
    return await notifyAudience('newQuestion', questionData, { kind: 'uids', uids: [questionData.assigneeId] });
  } catch (error) {
    console.error('Error sending question notification:', error);
    throw error;
  }
};

/**
 * Send fire notification to assigned people only (if they have new fire emails enabled).
 * Fires name their assignees, so the server matches names to staff and portal users.
 * @param {Object} fireData - The fire data
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendFireNotification = async (fireData) => {
  try {
    const names = Array.isArray(fireData.assignedTo) ? fireData.assignedTo : [];
    if (names.length === 0) {
      return { success: true, message: 'No assignees to notify', sentCount: 0 };
    }
    return await notifyAudience('newFire', fireData, { kind: 'names', names });
  } catch (error) {
    console.error('Error sending fire notification:', error);
    throw error;
  }
};

/**
 * Send task notification to assignees who have task notifications enabled (internal users
 * opt in; portal users are on unless they turned it off). The server adds the assignee and
 * creator names and photos the email shows.
 * @param {Object} taskData - The task data
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendTaskNotification = async (taskData) => {
  try {
    const uids = Array.isArray(taskData.assignees) ? taskData.assignees : [];
    if (uids.length === 0) {
      return { success: true, message: 'No assignees to notify', sentCount: 0 };
    }
    return await notifyAudience('newTask', taskData, { kind: 'uids', uids });
  } catch (error) {
    console.error('Error sending task notification:', error);
    throw error;
  }
};

/**
 * Send message notification to conversation participants only (the server skips the
 * sender), if they have new message emails enabled.
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendMessageNotification = async (messageData) => {
  try {
    const uids = Array.isArray(messageData.participants) ? messageData.participants : [];
    if (uids.length === 0) {
      return { success: true, message: 'No message recipients specified', sentCount: 0 };
    }
    return await notifyAudience('newMessage', messageData, { kind: 'uids', uids });
  } catch (error) {
    console.error('Error sending message notification:', error);
    throw error;
  }
};

/**
 * Notification templates and data mapping
 */
export const NOTIFICATION_TYPES = {
  newLead: {
    subject: 'New Lead Alert',
    template: 'newLead'
  },
  newClient: {
    subject: 'New Client Alert',
    template: 'newClient'
  },
  newMessage: {
    subject: 'New Message Alert',
    template: 'newMessage'
  },
  newQuestion: {
    subject: 'New Question Alert',
    template: 'newQuestion'
  },
  newFire: {
    subject: 'New Fire Alert',
    template: 'newFire'
  },
  newAnnouncement: {
    subject: 'New Announcement',
    template: 'newAnnouncement'
  },
  newTask: {
    subject: 'New Task Assignment',
    template: 'newTask'
  },
  technologyFulfillment: {
    subject: 'Technology Fulfillment Update',
    template: 'technologyFulfillment'
  },
  hrFulfillment: {
    subject: 'HR Fulfillment Update',
    template: 'hrFulfillment'
  },
  marketingFulfillment: {
    subject: 'Marketing Fulfillment Update',
    template: 'marketingFulfillment'
  },
  generalFulfillment: {
    subject: 'General Fulfillment Update',
    template: 'generalFulfillment'
  },
  communityPost: {
    subject: 'New Community Post',
    template: 'communityPost'
  },
  ndaSigned: {
    subject: 'NDA Signed Notification',
    template: 'ndaSigned'
  },
  contractSigned: {
    subject: 'Contract Signed Notification',
    template: 'contractSigned'
  },
  portalSignup: {
    subject: 'New Portal Signup',
    template: 'portalSignup'
  },
  summitRegistration: {
    subject: 'New Summit Registration',
    template: 'summitRegistration'
  },
  flaggedLead: {
    subject: 'Flagged Lead Alert',
    template: 'flaggedLead'
  },
  assessmentComplete: {
    subject: 'Assessment Complete',
    template: 'assessmentComplete'
  },
  clientCheckInComplete: {
    subject: 'Client Check In Completed',
    template: 'clientCheckInComplete'
  }
};

/**
 * Get notification display name
 * @param {string} notificationType - The notification type
 * @returns {string} Display name for the notification
 */
export const getNotificationDisplayName = (notificationType) => {
  const displayNames = {
    newLead: 'New Lead',
    newClient: 'New Client',
    newMessage: 'New Message',
    newQuestion: 'New Question',
    newFire: 'New Fire',
    newAnnouncement: 'New Announcement',
    newTask: 'New Task',
    completedTask: 'Completed Task',
    technologyFulfillment: 'Technology Update',
    hrFulfillment: 'HR Update',
    marketingFulfillment: 'Marketing Update',
    generalFulfillment: 'General Update',
    communityPost: 'Community Post',
    ndaSigned: 'NDA Signed',
    contractSigned: 'Contract Signed',
    portalSignup: 'Portal Signup',
    summitRegistration: 'Summit Registration',
    flaggedLead: 'Flagged Lead',
    assessmentComplete: 'Assessment Complete',
    clientCheckInComplete: 'Client Check In',
    newEvent: 'New Event',
    questionUpdate: 'Question Update',
    fireUpdate: 'Fire Update'
  };
  
  return displayNames[notificationType] || notificationType;
};
